import {
  mapShiftEventDto,
  mapPartialStoppage,
  isParameterChange,
  isTask,
  isSpot,
  isStoppage,
  useInforWorkOrderRequest,
  useMaintenanceNotification,
  useRequiredConfig
} from '@domain/shifthandover'
import {MIN_SPLIT_STOPPAGE_DURATION} from '@hconnect/common/consts'
import {StoppageDto, WorkOrderTarget, SapStatusCode, ShiftEvent} from '@hconnect/common/types'
import {useRequiredUser} from '@hconnect/login'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import {usePermission} from '@hproduce/permissions'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import DeleteIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import FileUpload from '@mui/icons-material/FileUpload'
import {SplitStoppageDialog} from '@widgets/common-running-times'
import moment from 'moment'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {hasMaintenanceNotificationCreationTimedOut} from '../common/utils/eventDetailsHelper'
import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'

import {ActionListItem, ActionList} from './ActionList'
import {CreateInforMaintenanceNotification} from './CreateInforMaintenanceNotification'
import {CreateSapMaintenanceNotification} from './CreateSapMaintenanceNotification'
import {EventDelete} from './EventDelete'

type EventActionListProps = {
  item: ShiftEvent
  doEdit: (item: ShiftEvent) => void
  doStoppageSplit: (initialStoppageData: Partial<ShiftEvent>) => void
  disableSap?: boolean
  hasChecklists?: boolean
  doClose: () => void
  onlyDeleteSeries?: boolean
  onSapNotificationCreated: () => void
  maintenanceNotificationFailed: boolean
  setMaintenanceNotificationFailed: (state: boolean) => void
  plantId: string
  onSapPmNotification?: (timezone: string) => void
}

export const EventActionList: React.FC<EventActionListProps> = (props) => {
  const {
    item,
    doEdit,
    disableSap,
    doClose,
    onlyDeleteSeries,
    onSapNotificationCreated,
    maintenanceNotificationFailed,
    setMaintenanceNotificationFailed,
    plantId
  } = props

  const [showDeleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [showSapNotificationDialog, setSapNotificationDialog] = useState<boolean>(false)
  const [showInforNotificationDialog, setInforNotificationDialog] = useState<boolean>(false)
  const [showSplitStoppageDialog, setSplitStoppageDialog] = useState<boolean>(false)

  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const {workOrderTarget, pxTrendAvailable, country} = useRequiredConfig(plantId)
  const plantContext = {plantId, countryId: country}
  const user = useRequiredUser()

  const isSapMaintenanceEnabled = useCommonFeatureFlag(FeatureFlagKey.SapMaintenanceNotification)
  const isInforMaintenanceEnabled = useCommonFeatureFlag(
    FeatureFlagKey.InforMaintenanceNotification
  )
  const isStoppageSubmitSupported = useCommonFeatureFlag(FeatureFlagKey.SubmitStoppage)
  const isSplitStoppageEnabled = useCommonFeatureFlag(FeatureFlagKey.EnableSplitStoppage)
  const isMaintenanceAllowed = usePermission({
    plantContext,
    permissionTypes: ['CREATE_SAP_REQUESTS']
  })
  const timezone = useUpmPlantTimezone(plantId)

  const canDeletePlantTasks = usePermission({plantContext, permissionTypes: ['DELETE_PLANT_TASKS']})

  const canDeletePlantEvents = usePermission({
    plantContext,
    permissionTypes: ['DELETE_PLANT_EVENTS']
  })

  const createMaintenanceNotification = useMaintenanceNotification({plantId})
  const createInforMaintenanceNotification = useInforWorkOrderRequest({plantId})

  const disableEdit = useMemo(() => {
    const isOwner = item.createdBy ? item.createdBy.id === user.id : true
    const isApprover =
      isParameterChange(item) && !!item?.approvers?.find((approver) => approver.user.id === user.id)

    return isParameterChange(item) && item.approvers?.length && !(isOwner || isApprover)
  }, [item, user.id])

  // eslint-disable-next-line complexity
  const actionListItems: ActionListItem[] = useMemo(() => {
    const items: ActionListItem[] = []

    if (!disableEdit) {
      items.push({
        label: t('shiftEvent.action.edit'),
        startIcon: <EditIcon />,
        onClick: () => doEdit(item),
        dataTestId: 'event-edit-button'
      })
    }

    if (isStoppage(item) && item.isSubmitted && isStoppageSubmitSupported) return items

    if (isStoppage(item) && isSplitStoppageEnabled) {
      const duration = moment
        .utc(item.stoppageEnd)
        .tz(timezone)
        .diff(moment.utc(item.stoppageStart).tz(timezone), 'minutes')
      if (duration > MIN_SPLIT_STOPPAGE_DURATION) {
        items.push({
          label: t('shiftEvent.action.split'),
          startIcon: <ContentCutIcon />,
          onClick: () => setSplitStoppageDialog(true),
          dataTestId: 'stoppage-split-button'
        })
      }
    }

    if (!isSpot(item) && (isTask(item) ? canDeletePlantTasks : canDeletePlantEvents)) {
      items.push({
        label: t('shiftEvent.action.delete'),
        startIcon: <DeleteIcon />,
        onClick: () => setDeleteDialog(true),
        dataTestId: 'event-delete-button'
      })
    }

    const isSapInSubmitState = item?.sapStatusCode === SapStatusCode.SUBMITTED
    const isAlreadySapConverted = item?.sapStatusCode && !isSapInSubmitState
    if (
      !disableSap &&
      !isAlreadySapConverted &&
      isSapMaintenanceEnabled &&
      isMaintenanceAllowed &&
      workOrderTarget === WorkOrderTarget.Sap
    ) {
      items.push({
        label: t('sapPm.action.createMaintenanceNotification'),
        startIcon: <FileUpload />,
        onClick: () => setSapNotificationDialog(true),
        dataTestId: 'sap-pm-dialog-mn',
        disabled:
          maintenanceNotificationFailed ||
          hasMaintenanceNotificationCreationTimedOut(item, moment.tz(timezone)),
        isLoading: createMaintenanceNotification.isPending || isSapInSubmitState
      })
    }

    const isAlreadyInforConverted = !!item?.inforWorkOrder?.requestId
    if (
      isInforMaintenanceEnabled &&
      isMaintenanceAllowed &&
      !isAlreadyInforConverted &&
      workOrderTarget === WorkOrderTarget.Infor
    ) {
      items.push({
        label: t('sapPm.action.createMaintenanceNotification'),
        startIcon: <FileUpload />,
        onClick: () => setInforNotificationDialog(true),
        dataTestId: 'infor-dialog-mn',
        disabled: maintenanceNotificationFailed,
        isLoading: createInforMaintenanceNotification.isPending
      })
    }

    return items
  }, [
    disableEdit,
    canDeletePlantEvents,
    canDeletePlantTasks,
    item,
    disableSap,
    isSapMaintenanceEnabled,
    isMaintenanceAllowed,
    workOrderTarget,
    isInforMaintenanceEnabled,
    t,
    doEdit,
    maintenanceNotificationFailed,
    timezone,
    createMaintenanceNotification.isPending,
    createInforMaintenanceNotification.isPending,
    isSplitStoppageEnabled,
    isStoppageSubmitSupported
  ])

  return (
    <>
      <ActionList plantId={plantId} listItems={actionListItems} />
      <EventDelete
        showDialog={showDeleteDialog}
        setDialogOpen={setDeleteDialog}
        onlyDeleteSeries={onlyDeleteSeries}
        event={item}
        onDelete={() => doClose()}
        plantId={plantId}
        country={country}
        hasChecklists={props.hasChecklists}
      />
      {showSapNotificationDialog && (
        <CreateSapMaintenanceNotification
          event={item}
          createNotification={createMaintenanceNotification}
          showDialog={showSapNotificationDialog}
          setDialog={setSapNotificationDialog}
          onNotificationCreated={onSapNotificationCreated}
          onError={() => setMaintenanceNotificationFailed(true)}
          plantId={plantId}
          onSapPmNotification={props.onSapPmNotification}
        />
      )}
      {showInforNotificationDialog && (
        <CreateInforMaintenanceNotification
          plantId={plantId}
          event={item}
          createNotification={createInforMaintenanceNotification}
          showDialog={showInforNotificationDialog}
          setDialog={setInforNotificationDialog}
          onNotificationCreated={onSapNotificationCreated}
          onError={() => setMaintenanceNotificationFailed(true)}
        />
      )}
      {isSplitStoppageEnabled && showSplitStoppageDialog && (
        <SplitStoppageDialog
          timezone={timezone}
          open={showSplitStoppageDialog}
          onClose={() => setSplitStoppageDialog(false)}
          stoppage={mapShiftEventDto(item) as StoppageDto}
          onSplitSubmit={(stoppage) =>
            props.doStoppageSplit(mapPartialStoppage(stoppage, timezone))
          }
          pxTrendAvailable={pxTrendAvailable}
        />
      )}
    </>
  )
}
